import React, { useEffect, useMemo, useState } from 'react';
import api from '../../../services/api';

// RDS sync status: public.sync_log (one row per scripts/sync_to_cloud.py run) with
// the per-table counts, errors and warnings that live in its `details` JSON.

const SYD = { timeZone: 'Australia/Sydney' };
const when = (iso) => (iso ? new Date(iso).toLocaleString('en-AU', { ...SYD, day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '–');
const ago = (iso) => {
  if (!iso) return '';
  const h = (Date.now() - new Date(iso).getTime()) / 3.6e6;
  return h < 1 ? `${Math.round(h * 60)}m ago` : h < 48 ? `${h.toFixed(0)}h ago` : `${(h / 24).toFixed(0)}d ago`;
};
const secs = (s) => (s == null ? '–' : s >= 90 ? `${(s / 60).toFixed(1)} min` : `${Number(s).toFixed(0)}s`);
const n = (v) => (v == null ? '–' : Number(v).toLocaleString('en-AU'));
const statusCls = (s) => (s === 'SUCCESS' ? 'ok' : s === 'COMPLETED_WITH_ERRORS' ? 'warn' : s ? 'bad' : 'muted');
const Pill = ({ s }) => <span className={`db-sync-pill ${statusCls(s)}`}>{s || '–'}</span>;

export default function SyncStatus() {
  const [list, setList] = useState(null);
  const [listError, setListError] = useState(null);
  const [runId, setRunId] = useState(null);
  const [run, setRun] = useState(null);
  const [runError, setRunError] = useState(null);
  const [tableSort, setTableSort] = useState({ col: 'row_diff', dir: 'desc' });
  const [onlyFlagged, setOnlyFlagged] = useState(false);

  useEffect(() => {
    api.get('/api/db-explorer/sync-log', { params: { limit: 60 } })
      .then((r) => { setList(r.data); setListError(null); if (r.data.runs?.length) setRunId(r.data.runs[0].id); })
      .catch((e) => setListError(e?.response?.data?.detail || e.message));
  }, []);

  useEffect(() => {
    if (runId == null) return undefined;
    let cancelled = false;
    setRun(null);
    api.get(`/api/db-explorer/sync-log/${runId}`)
      .then((r) => { if (!cancelled) { setRun(r.data); setRunError(null); } })
      .catch((e) => { if (!cancelled) setRunError(e?.response?.data?.detail || e.message); });
    return () => { cancelled = true; };
  }, [runId]);

  const median = list?.median_duration;
  const slow = (d) => median && d != null && d > 2 * median;

  const tables = useMemo(() => {
    if (!run) return [];
    let t = run.tables;
    if (onlyFlagged) t = t.filter((x) => x.status !== 'success' || x.mismatch || x.errors.length || x.warnings.length);
    const { col, dir } = tableSort;
    const sgn = dir === 'asc' ? 1 : -1;
    return [...t].sort((a, b) => {
      const av = col === 'row_diff' ? Math.abs(a.row_diff ?? 0) : a[col];
      const bv = col === 'row_diff' ? Math.abs(b.row_diff ?? 0) : b[col];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return (av > bv ? 1 : av < bv ? -1 : 0) * sgn;
    });
  }, [run, tableSort, onlyFlagged]);

  const sortBy = (col) => setTableSort((s) => ({ col, dir: s.col === col && s.dir === 'desc' ? 'asc' : 'desc' }));
  const Th = ({ col, children, left }) => (
    <th className={left ? 'l' : ''} style={{ cursor: 'pointer' }} onClick={() => sortBy(col)}>
      {children}{tableSort.col === col ? (tableSort.dir === 'desc' ? ' ▼' : ' ▲') : ''}
    </th>
  );

  if (listError) return <div className="alert alert-danger db-explorer-alert">Sync log unavailable: {listError}</div>;
  if (!list) return <div className="db-explorer-placeholder">Loading sync log…</div>;
  const latest = list.runs[0];
  const flaggedCount = run ? run.tables.filter((x) => x.status !== 'success' || x.mismatch || x.errors.length || x.warnings.length).length : 0;

  return (
    <div className="db-sync">
      <div className="db-sync-tiles">
        <div className="db-sync-tile">
          <div className="db-sync-tile-label">Last run</div>
          <div className="db-sync-tile-value">{when(latest?.sync_time)}</div>
          <div className="db-sync-tile-sub">{ago(latest?.sync_time)} · <Pill s={latest?.status} /> · {secs(latest?.duration_seconds)}{slow(latest?.duration_seconds) ? <span className="flag"> slow</span> : ''}</div>
        </div>
        <div className="db-sync-tile">
          <div className="db-sync-tile-label">Last clean run</div>
          <div className="db-sync-tile-value">{when(list.last_success)}</div>
          <div className="db-sync-tile-sub">{list.last_success ? ago(list.last_success) : 'none in the last ' + list.runs.length + ' runs'}</div>
        </div>
        <div className="db-sync-tile">
          <div className="db-sync-tile-label">Typical duration</div>
          <div className="db-sync-tile-value">{secs(median)}</div>
          <div className="db-sync-tile-sub">median of last {list.runs.length}; flagged above 2x</div>
        </div>
        {Object.entries(list.freshness).map(([sch, f]) => (
          <div className="db-sync-tile" key={sch}>
            <div className="db-sync-tile-label">{sch}</div>
            <div className="db-sync-tile-value">{ago(f.last_run)}</div>
            <div className="db-sync-tile-sub">{when(f.last_run)} · <Pill s={f.status} /></div>
          </div>
        ))}
      </div>

      <div className="db-sync-grid">
        <div className="db-sync-scroll">
          <table>
            <thead>
              <tr>
                <th className="l">Run (Sydney)</th><th className="l">Status</th><th className="l">Schemas</th>
                <th>Tables</th><th>Rows</th><th>Duration</th><th>Errors</th><th>Warnings</th><th>Failed</th><th>Skipped</th>
              </tr>
            </thead>
            <tbody>
              {list.runs.map((r) => (
                <tr key={r.id} className={`row-click${r.id === runId ? ' sel' : ''}`} onClick={() => setRunId(r.id)}>
                  <td className="l">{when(r.sync_time)}</td>
                  <td className="l"><Pill s={r.status} /></td>
                  <td className="l soft">{r.schemas.join(', ')}</td>
                  <td>{n(r.tables_synced)}</td>
                  <td>{n(r.rows_synced)}</td>
                  <td className={slow(r.duration_seconds) ? 'flag' : ''}>{secs(r.duration_seconds)}</td>
                  <td className={r.errors ? 'flag' : 'soft'}>{r.errors}</td>
                  <td className={r.warnings ? 'flag' : 'soft'}>{r.warnings}</td>
                  <td className={r.failed ? 'flag' : 'soft'}>{r.failed ?? '–'}</td>
                  <td className={r.skipped ? 'soft' : 'soft'}>{r.skipped ?? '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div>
          {runError && <div className="alert alert-warning db-explorer-alert">{runError}</div>}
          {!run && !runError && <div className="db-sync-section soft">Loading run…</div>}
          {run && (
            <>
              <div className="db-sync-section">
                <h3>Run {when(run.sync_time)} · <Pill s={run.status} /> · {secs(run.duration_seconds)} · {n(run.tables_synced)} tables, {n(run.rows_synced)} rows</h3>
                <div className="soft">successful {run.summary.successful ?? '–'} · skipped {run.summary.skipped ?? '–'} · failed {run.summary.failed ?? '–'}</div>
                {run.errors.length > 0 && (
                  <>
                    <h3 style={{ marginTop: 8 }}>Errors ({run.errors.length})</h3>
                    <ul className="db-sync-list">{run.errors.map((e, i) => <li key={i}>{String(e)}</li>)}</ul>
                  </>
                )}
                {run.warnings.length > 0 && (
                  <>
                    <h3 style={{ marginTop: 8 }}>Warnings ({run.warnings.length})</h3>
                    <ul className="db-sync-list">{run.warnings.map((e, i) => <li key={i}>{String(e)}</li>)}</ul>
                  </>
                )}
                {run.errors.length === 0 && run.warnings.length === 0 && <div className="soft" style={{ marginTop: 6 }}>No errors or warnings logged.</div>}
              </div>

              <div className="db-sync-section">
                <h3>
                  Tables ({run.tables.length}, {flaggedCount} flagged)
                  <label style={{ marginLeft: 12, fontWeight: 400 }}>
                    <input type="checkbox" checked={onlyFlagged} onChange={(e) => setOnlyFlagged(e.target.checked)} /> flagged only
                  </label>
                </h3>
                <div className="db-sync-scroll" style={{ maxHeight: '45vh' }}>
                  <table>
                    <thead>
                      <tr>
                        <Th col="table" left>Table</Th><Th col="status" left>Status</Th>
                        <Th col="source_count">Source</Th><Th col="dest_count_before">Dest before</Th><Th col="dest_count_after">Dest after</Th>
                        <Th col="row_diff">Row diff</Th><Th col="elapsed_seconds">Elapsed</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {tables.map((t) => (
                        <tr key={t.table}>
                          <td className="l">{t.table}{t.note ? <span className="soft" title={t.note}> *</span> : null}{t.errors.length ? <div className="flag" style={{ whiteSpace: 'normal', fontSize: 11 }}>{t.errors.join('; ')}</div> : null}{t.warnings.length ? <div className="soft" style={{ whiteSpace: 'normal', fontSize: 11 }}>{t.warnings.join('; ')}</div> : null}</td>
                          <td className="l"><span className={`db-sync-pill ${t.status === 'success' ? 'ok' : 'bad'}`}>{t.status || '–'}</span></td>
                          <td>{n(t.source_count)}</td>
                          <td className="soft">{n(t.dest_count_before)}</td>
                          <td className={t.mismatch ? 'flag' : ''}>{n(t.dest_count_after)}</td>
                          <td className={t.row_diff ? 'flag' : 'soft'}>{t.row_diff == null ? '–' : (t.row_diff > 0 ? '+' : '') + n(t.row_diff)}</td>
                          <td className="soft">{t.elapsed_seconds == null ? '–' : `${Number(t.elapsed_seconds).toFixed(1)}s`}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="soft" style={{ marginTop: 6, fontSize: 11.5 }}>
                  Row diff is the sync script's own figure. Dest after is flagged when it differs from the source count. Tables marked * are
                  reshaped on sync (Peer_BM_Performance is filtered, Correlation is unpivoted), so their counts differ by design and are not flagged.
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
